/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { DegreePlan_LearnerProgressState as LearnerProgressState } from '__generated__/graphql-types';

import { useRetracked } from 'js/lib/retracked';

import { Button, Hidden, ProductCard, breakpoints } from '@coursera/cds-core';

import CompletionMetadata from 'bundles/degree-home/components/v3-dpm/components/CourseCard/CompletionMetadata';
import CompletionStatus from 'bundles/degree-home/components/v3-dpm/components/CourseCard/CompletionStatus';
import EstimatedTermsOfferedIn from 'bundles/degree-home/components/v3-dpm/components/CourseCard/EstimatedTermsOfferedIn';
import Prerequisites from 'bundles/degree-home/components/v3-dpm/components/CourseCard/Prerequisites';
import TermsOfferedIn from 'bundles/degree-home/components/v3-dpm/components/CourseCard/TermsOfferedIn';
import UnknownAvailabilityDescription from 'bundles/degree-home/components/v3-dpm/components/CourseCard/UnknownAvailabilityDescription';
import EnrollmentDialog from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog';
import EnrollmentButton from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/EnrollmentButton';
import EnrollmentDiaglogEventingContext from 'bundles/degree-home/components/v3-dpm/components/EnrollmentDialog/EnrollmentDiaglogEventingContext';
import GoToCourseButton from 'bundles/degree-home/components/v3-dpm/components/GoToCourseButton';
import PlanningToolDialog from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/PlanningToolDialog';
import { useCoursePlanningDialog } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/context/CoursePlanningDialogContext';
import {
  ADD_TO_PLAN_BUTTON,
  VIEW_PLAN_BUTTON,
} from 'bundles/degree-home/components/v3-dpm/constants/trackingNameConstants';
import type { UseCurriculum_PersonalizedSegmentCourseFragment as SegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import { degreeV3EventShouldBeShown } from 'bundles/degree-home/components/v3-dpm/lib/experiments';
import { checkCourseHasUnknownAvailability } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import CreditCount from 'bundles/program-loggedin-shared/components/CreditCount';
import { JoinedMetadata } from 'bundles/program-loggedin-shared/components/Metadata';
import RequirementType from 'bundles/program-loggedin-shared/components/RequirementType';

import DefaultCourseImg from 'images/degree-home/default-course-logo.png';

import _t from 'i18n!nls/degree-home';

interface CourseCardProps {
  course: SegmentCourse;
  isElective: boolean;
}

interface CourseCardInnerProps extends CourseCardProps {
  showImage: boolean;
}

const styles = {
  actionButtons: css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
  // TODO: Orest to remove in CDS v1.32.3
  overrideCdsImageSize: css`
    margin-top: var(--cds-spacing-100);

    ${breakpoints.up('lg')} {
      .cds-ProductCard-listPreviewImage {
        aspect-ratio: 1/1 !important;
        max-height: var(--list-img-size) !important;
        height: var(--list-img-size) !important;
        flex: none;

        img {
          width: var(--list-img-size) !important;
          height: var(--list-img-size) !important;
        }
      }
    }
  `,
};

const createTrackingData = (courseName: string, isElective: boolean, creditValue: number) => {
  const requirementType = isElective ? 'Elective' : 'Required';
  return {
    courseName,
    requirementType,
    courseCreditValue: creditValue,
  };
};

/** Exported for testing. Rendered twice, and conditionally hidden with CSS. */
export const CourseCardInner: React.VFC<CourseCardInnerProps> = ({ course, isElective, showImage }) => {
  const { segmentCourse, learnerProgress } = course;
  const { courseraCourse, courseCode } = segmentCourse.course;
  const { openCourseDialog } = useCoursePlanningDialog();

  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const [isPlanOpen, setPlanModalOpen] = React.useState(false);

  const track = useRetracked();

  const courseName = segmentCourse.course.courseName;

  const courseHasUnknownAvailability = checkCourseHasUnknownAvailability(segmentCourse);
  const courseHasNonEmptyAvailability = (segmentCourse.termAvailabilityPaginated?.elements ?? []).length > 0;

  const showEnrollmentButton =
    learnerProgress.state === LearnerProgressState.NotStarted && degreeV3EventShouldBeShown();

  const showViewPlanModal = learnerProgress.state === LearnerProgressState.Planned;
  const showAddToPlanButton =
    learnerProgress.state === LearnerProgressState.NotStarted && courseHasNonEmptyAvailability; // if the course's availability is empty, the "Add to plan" button should be hidden so learners don't see an empty list in the "Add to plan" dialog

  const trackingData = createTrackingData(courseName, isElective, segmentCourse.creditValue);

  const termsOfferingComponent = (
    <EstimatedTermsOfferedIn
      termAvailability={segmentCourse.termAvailabilityPaginated?.elements}
      state={learnerProgress.state}
    />
  );

  const handleOpenDialog = (trackingName: string, openDialogFunction: () => void, includeCourseData: boolean) => {
    const courseData = includeCourseData ? trackingData : {};
    track({
      action: 'click',
      trackingName,
      trackingData: courseData,
    });
    openDialogFunction();
  };

  const handleOpenEnrollmentDialog = (clickLocation: 'card' | 'button') => {
    const trackingName = clickLocation === 'card' ? 'course_card' : 'course_enrollment_options';
    handleOpenDialog(trackingName, () => setModalIsOpen(true), false);
  };

  const handleViewPlanClick = () => {
    handleOpenDialog(VIEW_PLAN_BUTTON, () => setPlanModalOpen(true), true);
  };

  const handleAddCourseToPlanDialog = () => {
    handleOpenDialog(ADD_TO_PLAN_BUTTON, () => openCourseDialog(course, 'add'), true);
  };

  return (
    <React.Fragment>
      <ProductCard
        variant="list"
        productType="Course"
        previewImageSrc={showImage ? courseraCourse?.promoPhotoUrl || DefaultCourseImg : undefined}
        title={{
          name: _t('#{courseCode} - #{courseName}', {
            courseName,
            courseCode,
          }),
        }}
        css={styles.overrideCdsImageSize}
        onClick={(e) => {
          e.stopPropagation();

          if (!showEnrollmentButton) {
            return;
          }
          handleOpenEnrollmentDialog('card');
        }}
        body={
          <React.Fragment>
            <CompletionStatus state={learnerProgress.state} />
            <Prerequisites prerequisite={segmentCourse.prerequisite} />
            <JoinedMetadata>
              <RequirementType key="RequirementType" isElective={isElective} />
              <CreditCount key="CreditCount" credits={segmentCourse.creditValue} />
            </JoinedMetadata>
            <CompletionMetadata learnerProgress={learnerProgress} />
            {courseHasUnknownAvailability ? <UnknownAvailabilityDescription /> : termsOfferingComponent}
          </React.Fragment>
        }
        adminContent={
          <div css={styles.actionButtons}>
            {showEnrollmentButton && (
              <EnrollmentButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEnrollmentDialog('button');
                }}
                course={course}
              />
            )}

            <GoToCourseButton course={course} />

            {showAddToPlanButton && (
              <Button
                variant="ghost"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddCourseToPlanDialog();
                }}
              >
                {_t('Add to plan')}
              </Button>
            )}

            {showViewPlanModal && (
              <Hidden mdUp>
                <Button variant="ghost" size="small" onClick={handleViewPlanClick}>
                  {_t('View plan')}
                </Button>
              </Hidden>
            )}
          </div>
        }
      />
      <EnrollmentDiaglogEventingContext segmentCourse={course.segmentCourse}>
        <EnrollmentDialog course={course} modalIsOpen={modalIsOpen} closeModal={() => setModalIsOpen(false)} />
      </EnrollmentDiaglogEventingContext>

      <PlanningToolDialog open={isPlanOpen} closeModal={() => setPlanModalOpen(false)} />
    </React.Fragment>
  );
};

/**
 * The main entrypoint for the course card.
 *
 * It conditionally renders two versions of the inner card depending on the device size category.
 * See [this thread](https://github.com/webedx-spark/web/pull/31317/files#r1265546446) for more discussion on this choice.
 */
const CourseCard: React.VFC<CourseCardProps> = (props) => {
  return (
    <React.Fragment>
      <Hidden smUp>
        <CourseCardInner {...props} showImage={false} />
      </Hidden>
      <Hidden xsDown>
        <CourseCardInner {...props} showImage={true} />
      </Hidden>
    </React.Fragment>
  );
};

export default CourseCard;
